import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Box,
  Breadcrumbs,
  Button,
  Card,
  Group,
  Stack,
  Table,
  Title,
  Anchor,
} from "@mantine/core";

import {
  ADMIN_ORGANIZATION,
  PLACE_CREATE_AUTHORISED_ORGANIZATIONS,
  PLACE_CREATE_AUTHORISED_ROLES,
  PLACE_ORGANIZATION_VISIBILITY_AUTHORISED_ORGANIZATIONS,
  PLACE_ORGANIZATION_VISIBILITY_AUTHORISED_ROLES,
} from "_roles";

import type { Place } from "place/types/place.model";
import { AccessRights } from "common/admin/auth/AccessRights";
import { useOrganization } from "common/admin/auth/hooks";
import QueryWrapper from "common/components/QueryWrapper";
import { Th } from "common/components/table/Th";

import {
  useListPlacesQuery,
  useRemovePlaceMutation,
} from "place/api/place.api";

import { PlaceAdminRow } from "../components/PlaceAdminRow";
import { ADMIN_PATH } from "_urls";

type SortedKeys = keyof Pick<Place, "owner" | "name" | "creationTime">;

function sortData(
  data: Place[],
  payload: { sortBy: SortedKeys; reversed: boolean },
): Place[] {
  return [...data].sort((a, b) => {
    if (payload.reversed) {
      return String(b[payload.sortBy]).localeCompare(String(a[payload.sortBy]));
    }
    return String(a[payload.sortBy]).localeCompare(String(b[payload.sortBy]));
  });
}

export function PlacesListAdminView() {
  const navigate = useNavigate();
  const listPlacesQuery = useListPlacesQuery();
  const [sortedData, setSortedData] = useState<Place[]>([]);
  const [sortBy, setSortBy] = useState<SortedKeys>("creationTime");
  const [reverseSortDirection, setReverseSortDirection] = useState(false);
  const [organization] = useOrganization();

  const places = useMemo(() => listPlacesQuery.data, [listPlacesQuery.data]);

  const filteredPlaces = useMemo(
    () => (places || []).filter((place) => place.creationTime && place.name),
    [places],
  );

  const [deletePlace] = useRemovePlaceMutation();

  const handleDelete = async (id: string) => {
    try {
      await deletePlace(id).unwrap();
      console.log(`Place ${id} deleted successfully`);
    } catch (error) {
      console.error("Failed to delete place", error);
    }
  };

  const triggerSortingData = useCallback(
    (field: SortedKeys) => {
      const reversed = field === sortBy ? !reverseSortDirection : false;
      return sortData(filteredPlaces, { sortBy: field, reversed });
    },
    [filteredPlaces, reverseSortDirection, sortBy],
  );

  useEffect(() => {
    setSortedData(triggerSortingData(sortBy));
  }, [filteredPlaces, sortBy, triggerSortingData]);

  return (
    <Box>
      <div
        className="sticky top-0 z-50 pb-4"
        style={{ backgroundColor: "#E9ECEF" }}
      >
        <Title order={3} align="center" m="xs">
          Places
        </Title>
        <Breadcrumbs separator="→">
          <Anchor component={Link} to={`/${ADMIN_PATH}`}>
            Admin
          </Anchor>
          <span>Places</span>
        </Breadcrumbs>

        <AccessRights
          authorizedOrganizations={PLACE_CREATE_AUTHORISED_ORGANIZATIONS}
          authorizedRoles={PLACE_CREATE_AUTHORISED_ROLES}
        >
          <Group position="right">
            <Button className="bg-button-admin" component={Link} to="new">
              New
            </Button>
          </Group>
        </AccessRights>
      </div>

      <Card shadow={"sm"} p={"xl"} className={"overflow-visible"}>
        <QueryWrapper query={listPlacesQuery}>
          {filteredPlaces && filteredPlaces.length > 0 ? (
            <Table highlightOnHover>
              <thead>
                <tr>
                  <Th
                    reversed={reverseSortDirection}
                    sorted={sortBy === "name"}
                    onClick={() => {
                      if (sortBy === "name") {
                        setReverseSortDirection(!reverseSortDirection);
                      } else {
                        setSortBy("name");
                      }
                    }}
                  >
                    Name
                  </Th>
                  <AccessRights
                    authorizedOrganizations={
                      PLACE_ORGANIZATION_VISIBILITY_AUTHORISED_ORGANIZATIONS
                    }
                    authorizedRoles={
                      PLACE_ORGANIZATION_VISIBILITY_AUTHORISED_ROLES
                    }
                  >
                    <Th
                      reversed={reverseSortDirection}
                      sorted={sortBy === "owner"}
                      onClick={() => {
                        if (sortBy === "owner") {
                          setReverseSortDirection(!reverseSortDirection);
                        } else {
                          setSortBy("owner");
                        }
                      }}
                    >
                      Organization
                    </Th>
                  </AccessRights>
                  <Th
                    reversed={reverseSortDirection}
                    sorted={sortBy === "creationTime"}
                    onClick={() => {
                      if (sortBy === "creationTime") {
                        setReverseSortDirection(!reverseSortDirection);
                      } else {
                        setSortBy("creationTime");
                      }
                    }}
                  >
                    Creation Date
                  </Th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortedData.map((place) =>
                  place.owner.L1 === organization ||
                  organization === ADMIN_ORGANIZATION ? (
                    <PlaceAdminRow
                      key={place.id}
                      place={place}
                      onEdit={() => navigate(place.id)}
                      onDelete={() => handleDelete(place.id)}
                      onDuplicate={() => navigate(`${place.id}/duplicate`)}
                    />
                  ) : null,
                )}
              </tbody>
            </Table>
          ) : (
            <Stack align="center">
              <Box>No places found</Box>
              <Button className="bg-button-admin" component={Link} to="new">
                New
              </Button>
            </Stack>
          )}
        </QueryWrapper>
      </Card>
    </Box>
  );
}
